"use client";

import { setReferralSignupCookies } from "@/app/(platform)/researchers/login/actions";
import { setSignupSourceCookie } from "@/app/actions/signup-source";
import { GoogleIcon, MicrosoftIcon } from "@/components/app/brand-icons";
import { AUDIENCE_HERO } from "@/components/auth/audience-hero";
import {
  OAuthSignIn,
  type OAuthSignInConfig,
} from "@/components/auth/oauth-sign-in";
import { setSignupIntent } from "@/lib/signup-intent";

interface ResearcherSignInProps {
  returnUrl?: string;
  initialEmail?: string;
  // Present when the page was reached via a business share link (?ref=<token>).
  referralToken?: string;
}

export function ResearcherSignIn({
  returnUrl,
  initialEmail,
  referralToken,
}: ResearcherSignInProps) {
  const hero = AUDIENCE_HERO.researcher;

  const config: OAuthSignInConfig = {
    // Same panel step 1 previewed when this side was picked, so the two steps
    // read as one flow rather than two screens.
    heroAudience: "researcher",
    heroHeadline: hero.headline,
    heroSubtitle: hero.subtitle,
    heroPanelClass: hero.panelClass,
    heading: "Sign in as a Researcher",
    // /login is the expert sign-in, so this is the only way across between the
    // two audiences now that the gate is gone.
    // Speaks to what an expert is here for rather than naming the role: the
    // person who lands on the researcher form by mistake is looking to be
    // interviewed, not to identify as an "expert".
    crossLink: {
      href: "/login",
      prompt: "Looking for paid interviews?",
      label: "Log in to take part",
    },
    // With magic link as the primary action, Google and Microsoft render as an
    // icon row, so size Google to match the other row icons.
    primary: {
      id: "google",
      label: "Google",
      icon: <GoogleIcon className="size-[27px]" />,
    },
    secondaries: [
      {
        id: "microsoft",
        label: "Microsoft",
        icon: <MicrosoftIcon className="size-[27px]" />,
      },
    ],
    // Passwordless email sign-in.
    magicLink: { initialEmail },
    // New researchers land on /researchers/welcome to name their org; the
    // welcome step forwards returning researchers straight to their dashboard.
    finalRedirect: returnUrl || "/researchers/welcome",
    onBeforeSignIn: async () => {
      // Set referral + business-intent cookies here (awaited before the sign-in
      // redirect) rather than in a page effect, which has no ordering guarantee
      // against the bounce.
      // An expert token reaching this page is someone who was sent a panelist
      // link; they belong on the participant side, not in book-a-call.
      const audience = referralToken
        ? (await setReferralSignupCookies(referralToken)).audience
        : "business";
      await setSignupSourceCookie("researcher_login_page");
      setSignupIntent(audience === "expert" ? "participant" : "researcher");
    },
  };

  return <OAuthSignIn config={config} />;
}
